import {
  useState, useMemo, useEffect, useLayoutEffect, cloneElement, useRef, useCallback,
} from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { queryFilters, withInclusiveDateStart } from '../../../../utils/queryFilters';
import S from './ItemsListStyles';
import { DataDetails } from '../../ItemsList/types';
import { FeaturesManagement } from '../../../../featuresControl';
import ReportRequest from '../../ReportRequest/ReportRequest';
import { H2 } from '../../../ui/H2/H2';
import useGetDriversAvatarsBulk from '../../../../API/hooks/drivers/useGetDriversAvatarsBulk';
import { DriverAvatar } from '../../../../API/types/Drivers';
import { logger } from '../../../../API/services/SharedService';
import { getPeriodDisplayLabel } from '../../../../utils/dateUtils';
import { CalendarItemType } from '../types';
import {
  ContainerScrollPosition,
  getRestorableContainerPosition,
  saveContainerScrollPosition,
} from '../../../../utils/scrollRestoration';

const generateKey = (item: DataDetails): string => `${item._id} ${item?.updatedAt ? item.updatedAt : ''}`;

const getAvatarByDriverId = (avatars: DriverAvatar[], driverId: string): string | null => {
  const driversAvatars = avatars.filter((d: any) => d.driverId === driverId);
  return driversAvatars.length ? driversAvatars[0].avatarUrl : null;
};

const getTripDriverId = (trip: any): string | undefined => (
  trip?.driverId || trip?.driver?.driverId
);
const getEntityTitle = (entity: string): string => {
  if (entity === 'calendar') {
    return 'timeline events';
  }
  return entity.endsWith('y') ? `${entity.slice(0, -1)}ies` : `${entity}s`;
};

const SCROLL_LOAD_THRESHOLD_PX = 100;
const SCROLL_RESTORE_TIMEOUT_MS = 8000;

const isNearScrollBottom = (element: HTMLDivElement): boolean => (
  element.scrollHeight - element.scrollTop - element.clientHeight <= SCROLL_LOAD_THRESHOLD_PX
);

type Props = {
  fleetId: string;
  payload: any;
  listEntity: string;
  customQuery?: string;
  children: JSX.Element;
  getQuery: (fleetId: string, payload?: any, filters?: string, deps?: any) => UseInfiniteQueryResult<any, any>;
  isRow?: boolean;
  filters?: Record<string, string[]>;
  deps?: any[];
  hasCreateReport?: boolean;
  reportEndpoint?: any;
  reportName?: string;
  extraChildProps?: Record<string, any>;
  hidePeriodInListHeading?: boolean;
  calendarSeedItems?: CalendarItemType[];
  inclusiveEventDateStart?: boolean;
};

function ItemsList({
  fleetId,
  listEntity,
  customQuery,
  children,
  getQuery,
  filters,
  payload,
  deps,
  isRow,
  hasCreateReport,
  reportEndpoint,
  reportName,
  extraChildProps,
  hidePeriodInListHeading = false,
  calendarSeedItems,
  inclusiveEventDateStart = false,
}: Props): JSX.Element {
  const [localFilters] = useState<Record<string, string[]>>({});
  const [avatars, setAvatars] = useState<DriverAvatar[]>([]);
  const pendingDriverIdsRef = useRef<Set<string>>(new Set());
  const currentFetchDriverIdsRef = useRef<string[]>([]);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const scrollSnapshotRef = useRef({ scrollTop: 0, scrollHeight: 0, dataLength: 0 });
  const location = useLocation();
  const navigationType = useNavigationType();
  const locationRef = useRef(location);
  const loadedPageCountRef = useRef(0);
  const restoreTargetRef = useRef<ContainerScrollPosition | null | undefined>(undefined);
  const restoreDeadlineRef = useRef(0);

  locationRef.current = location;

  if (restoreTargetRef.current === undefined) {
    restoreTargetRef.current = getRestorableContainerPosition(
      location,
      navigationType,
      listEntity,
    ) || null;
    restoreDeadlineRef.current = Date.now() + SCROLL_RESTORE_TIMEOUT_MS;
  }

  const isRestoringRef = useRef(restoreTargetRef.current !== null);
  const dateFieldMap: Record<string, string> = {
    alert: 'eventDate', trip: 'startAt', driver: 'startAt', vehicle: 'startAt',
  };
  const dateFieldByEntity = dateFieldMap[listEntity];
  const effectiveFilters = listEntity === 'video'
    ? Object.fromEntries(Object.entries(filters || localFilters).filter(([k]) => k !== 'startDate' && k !== 'endDate'))
    : (filters || localFilters);
  const shouldUseInclusiveEventDateStart = inclusiveEventDateStart
    && (listEntity === 'alert' || listEntity === 'calendar');
  const buildQuery = useCallback((page: number): string => {
    const query = queryFilters(
      effectiveFilters,
      page,
      customQuery,
      dateFieldByEntity,
    );
    if (!shouldUseInclusiveEventDateStart) {
      return query;
    }
    return withInclusiveDateStart(query, dateFieldByEntity || 'createdAt');
  }, [
    effectiveFilters,
    customQuery,
    dateFieldByEntity,
    shouldUseInclusiveEventDateStart,
  ]);
  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    remove,
  } = getQuery(fleetId, payload, buildQuery(1), deps);
  const allData = useMemo(() => {
    const fetched = data?.pages?.flatMap((item: any) => item?.results || item) ?? [];
    if (listEntity !== 'calendar' || !calendarSeedItems?.length) {
      return fetched;
    }

    const mergedById = new Map<string, CalendarItemType>();
    [...calendarSeedItems, ...(fetched as CalendarItemType[])].forEach((item) => {
      if (item?._id) {
        mergedById.set(item._id, item);
      }
    });

    return Array.from(mergedById.values()).sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [calendarSeedItems, data, listEntity]);
  const loadedPageCount = data?.pages?.length ?? 0;
  loadedPageCountRef.current = loadedPageCount;

  const onSubmitDriversAvatarsSuccess = (a: DriverAvatar[]): void => {
    const fetchedByDriverId = new Map(a.map((avatar) => [avatar.driverId, avatar]));
    currentFetchDriverIdsRef.current.forEach((driverId) => {
      pendingDriverIdsRef.current.delete(driverId);
    });

    setAvatars((prevAvatars) => {
      const mergedAvatars = [...prevAvatars];
      currentFetchDriverIdsRef.current.forEach((driverId) => {
        const avatar = fetchedByDriverId.get(driverId) || { driverId, avatarUrl: null };
        if (!mergedAvatars.some((existing) => existing.driverId === driverId)) {
          mergedAvatars.push(avatar);
        }
      });
      return mergedAvatars;
    });
    currentFetchDriverIdsRef.current = [];
  };

  const onSubmitDriversAvatarsFail = (err: string): void => {
    currentFetchDriverIdsRef.current.forEach((driverId) => {
      pendingDriverIdsRef.current.delete(driverId);
    });
    currentFetchDriverIdsRef.current = [];
    logger.error('getDriversAvatarsBulk', 'On get fail', err);
  };

  const { mutate: getDriversAvatarsBulk, isLoading: isDriversAvatarsBulkLoading } = useGetDriversAvatarsBulk(
    onSubmitDriversAvatarsSuccess,
    onSubmitDriversAvatarsFail,
  );

  const extendDriverProperties = (item: any): any => {
    const driverId = getTripDriverId(item);
    if (!item?.driver || !driverId) {
      return item;
    }

    return {
      ...item,
      driver: {
        ...item.driver,
        avatarUrl: getAvatarByDriverId(avatars, driverId),
      },
    };
  };

  useEffect(() => {
    if (
      (listEntity !== 'trip' && listEntity !== 'driver')
      || !fleetId
      || isLoading
      || isDriversAvatarsBulkLoading
      || !allData?.length
    ) {
      return;
    }

    const loadedDriverIds = new Set(avatars.map((avatar) => avatar.driverId));
    const driverIdsToFetch = Array.from(new Set(
      allData
        .map(getTripDriverId)
        .filter((driverId): driverId is string => (
          !!driverId
          && !loadedDriverIds.has(driverId)
          && !pendingDriverIdsRef.current.has(driverId)
        )),
    ));

    if (!driverIdsToFetch.length) {
      return;
    }

    driverIdsToFetch.forEach((driverId) => pendingDriverIdsRef.current.add(driverId));
    currentFetchDriverIdsRef.current = driverIdsToFetch;
    getDriversAvatarsBulk({
      fleetId,
      driverIds: driverIdsToFetch,
    });
  }, [
    allData,
    avatars,
    fleetId,
    getDriversAvatarsBulk,
    isDriversAvatarsBulkLoading,
    isLoading,
    listEntity,
  ]);
  const showAlertHeading = listEntity === 'alert' && !isLoading && allData && allData.length > 0;
  const showTripHeading = listEntity === 'trip' && !isLoading && (data?.pages?.[0]?.total ?? 0) > 0;
  const showVehicleHeading = listEntity === 'vehicle' && !isLoading && allData && allData.length > 0;
  const showDriverHeading = listEntity === 'driver' && !isLoading && allData && allData.length > 0;
  const showCalendarHeading = listEntity === 'calendar' && !isLoading && allData && allData.length > 0;
  const showVideoHeading = listEntity === 'video' && !isLoading && allData && allData.length > 0;
  const showConnectivityHeading = listEntity === 'connectivity' && !isLoading && allData && allData.length > 0;
  const showTotalHeading = showAlertHeading || showTripHeading || showVehicleHeading || showDriverHeading
    || showCalendarHeading || showVideoHeading || showConnectivityHeading;
  // getPeriodDisplayLabel needs the real startDate/endDate, so it reads the unfiltered `filters`
  // prop rather than `effectiveFilters` (which strips those fields for video queries).
  const periodLabel = getPeriodDisplayLabel(filters || localFilters);
  const headingPeriodSuffix = hidePeriodInListHeading ? '' : ` • ${periodLabel}`;
  const alertCount = data?.pages?.[0]?.total ?? 0;
  const tripCount = data?.pages?.[0]?.total ?? 0;
  const vehicleCount = allData?.length ?? 0;
  const driverCount = allData?.length ?? 0;
  const videoCount = allData?.length ?? 0;
  const connectivityCount = allData?.length ?? 0;
  let totalHeadingText = `${alertCount} ${alertCount === 1 ? 'alert' : 'alerts'}${headingPeriodSuffix}`;
  if (showTripHeading) {
    totalHeadingText = `${tripCount} ${tripCount === 1 ? 'trip' : 'trips'}${headingPeriodSuffix}`;
  } else if (showVehicleHeading) {
    totalHeadingText = `${vehicleCount} ${vehicleCount === 1 ? 'vehicle' : 'vehicles'}${headingPeriodSuffix}`;
  } else if (showDriverHeading) {
    totalHeadingText = `${driverCount} ${driverCount === 1 ? 'driver' : 'drivers'}${headingPeriodSuffix}`;
  } else if (showCalendarHeading) {
    totalHeadingText = hidePeriodInListHeading ? 'Timeline' : `Timeline • ${periodLabel}`;
  } else if (showVideoHeading) {
    totalHeadingText = `${videoCount} ${videoCount === 1 ? 'video' : 'videos'}${headingPeriodSuffix}`;
  } else if (showConnectivityHeading) {
    totalHeadingText = `${connectivityCount} ${connectivityCount === 1 ? 'entry' : 'entries'}${headingPeriodSuffix}`;
  }

  const filterDepKey = JSON.stringify(deps?.[0] ?? null);
  const lastFilterDepKeyRef = useRef(filterDepKey);
  useEffect(() => {
    if (lastFilterDepKeyRef.current !== filterDepKey) {
      lastFilterDepKeyRef.current = filterDepKey;
      restoreTargetRef.current = null;
      isRestoringRef.current = false;
    }
    if (remove) {
      remove();
    }
    setAvatars([]);
    pendingDriverIdsRef.current.clear();
    currentFetchDriverIdsRef.current = [];
    scrollSnapshotRef.current = { scrollTop: 0, scrollHeight: 0, dataLength: 0 };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDepKey]);

  const updateScrollSnapshot = useCallback((): void => {
    const scrollRoot = listContainerRef.current;
    if (!scrollRoot) return;
    scrollSnapshotRef.current = {
      scrollTop: scrollRoot.scrollTop,
      scrollHeight: scrollRoot.scrollHeight,
      dataLength: allData?.length ?? 0,
    };
  }, [allData?.length]);

  const handleNextPage = useCallback((): void => {
    if (isLoading || isFetchingNextPage || !hasNextPage) return;
    fetchNextPage({
      pageParam: buildQuery(loadedPageCount + 1),
    });
  }, [
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    buildQuery,
    loadedPageCount,
  ]);

  const tryLoadNextPageFromScroll = useCallback((): void => {
    const scrollRoot = listContainerRef.current;
    if (!scrollRoot || !isNearScrollBottom(scrollRoot)) return;
    handleNextPage();
  }, [handleNextPage]);

  useEffect(() => {
    const scrollRoot = listContainerRef.current;
    if (!scrollRoot) return undefined;

    const onScroll = (): void => {
      tryLoadNextPageFromScroll();
      updateScrollSnapshot();

      if (!isRestoringRef.current) {
        saveContainerScrollPosition(locationRef.current, listEntity, {
          scrollTop: scrollRoot.scrollTop,
          pageCount: loadedPageCountRef.current,
        });
      }
    };

    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    updateScrollSnapshot();
    return () => scrollRoot.removeEventListener('scroll', onScroll);
  }, [listEntity, tryLoadNextPageFromScroll, updateScrollSnapshot]);

  useEffect(() => {
    if (!hasNextPage) return;
    tryLoadNextPageFromScroll();
  }, [allData?.length, isFetchingNextPage, isFetching, isLoading, hasNextPage, tryLoadNextPageFromScroll]);

  useLayoutEffect(() => {
    if (listEntity === 'calendar' || isRestoringRef.current) return;

    const scrollRoot = listContainerRef.current;
    if (!scrollRoot || !allData?.length) return;

    const prev = scrollSnapshotRef.current;
    if (prev.dataLength === allData.length) return;

    const wasAtBottom = prev.scrollHeight > 0
      && prev.scrollTop + scrollRoot.clientHeight >= prev.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;

    if (prev.dataLength > 0 && !wasAtBottom && scrollRoot.scrollHeight > prev.scrollHeight) {
      scrollRoot.scrollTop = prev.scrollTop + (scrollRoot.scrollHeight - prev.scrollHeight);
    }

    scrollSnapshotRef.current = {
      scrollTop: scrollRoot.scrollTop,
      scrollHeight: scrollRoot.scrollHeight,
      dataLength: allData.length,
    };
  }, [allData?.length, listEntity]);

  const finishScrollRestore = useCallback((scrollTop?: number): void => {
    isRestoringRef.current = false;
    restoreTargetRef.current = null;

    const scrollRoot = listContainerRef.current;
    if (scrollRoot && scrollTop !== undefined) {
      const maxScrollTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
      scrollRoot.scrollTop = Math.min(scrollTop, maxScrollTop);
    }

    updateScrollSnapshot();
  }, [updateScrollSnapshot]);

  useLayoutEffect(() => {
    if (!isRestoringRef.current) return;

    const target = restoreTargetRef.current;
    if (!target) {
      finishScrollRestore();
      return;
    }

    if (Date.now() > restoreDeadlineRef.current) {
      finishScrollRestore(target.scrollTop);
      return;
    }

    if (isLoading || isFetchingNextPage || !allData?.length) return;

    if (loadedPageCount < target.pageCount && hasNextPage) {
      handleNextPage();
      return;
    }

    finishScrollRestore(target.scrollTop);
  }, [
    allData?.length,
    loadedPageCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    handleNextPage,
    finishScrollRestore,
  ]);

  useEffect(() => {
    if (!isRestoringRef.current) return undefined;

    const timeoutId = setTimeout(() => {
      if (isRestoringRef.current) {
        finishScrollRestore(restoreTargetRef.current?.scrollTop);
      }
    }, SCROLL_RESTORE_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [finishScrollRestore]);

  return (
    <S.Wrapper $note={listEntity === 'note'}>
      {hasCreateReport && FeaturesManagement.features.report && (
        <div className="report-button">
          <ReportRequest fleetId={fleetId} name={reportName} endpoint={reportEndpoint} query={buildQuery(loadedPageCount || 1)} />
        </div>
      )}
      {showTotalHeading && (
        <div className="alert-total">
          <H2>{totalHeadingText}</H2>
        </div>
      )}
      <div
        ref={listContainerRef}
        className={`entities-list ${listEntity === 'note' ? 'note' : ''} ${showTotalHeading ? 'with-alert-total' : ''}`}
      >
        {isLoading && <div> Loading... </div>}
        {!isLoading && allData && (
          <>
            {!isLoading && !allData.length && <div className="no-data">No {getEntityTitle(listEntity)} found</div>}
            <ul className={`list-container ${isRow ? 'row' : ''}`}>
              {!!allData.length
                && allData.map((item: DataDetails) => cloneElement(children, {
                  key: generateKey(item),
                  item: (listEntity === 'trip' || listEntity === 'driver') ? extendDriverProperties(item) : item,
                  ...extraChildProps,
                }))}
            </ul>
            {isFetchingNextPage && hasNextPage && <div className="list-loading"> Loading... </div>}
          </>
        )}
      </div>
    </S.Wrapper>
  );
}

export default ItemsList;
